import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import {
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import politic from "public/politic.svg";
import social from "public/social.svg";
import economic from "public/economic.svg";
import prophetic from "public/prophetic.svg";
import finance from "public/finance.svg";
import sport from "public/sport.svg";
import scientific from "public/scientific.svg";
import usePromises from "hooks/promises/usePromises";
import PromiseCard from "components/card/PromiseCard";
import PromiseShortsCard from "components/card/PromiseShortsCard";
import InfiniteScrollTrigger from "components/infinite-scroll/InfiniteScrollTrigger";
import { skeletonGenerator } from "components/infinite-scroll/helpers";
import ProgramSkeleton from "components/infinite-scroll/skeletons/ArticleSkeleton";
import CustomHead from "components/head/CustomHead";
import { getPromises } from "api-algolia/promises.api";
import useSearch from "hooks/algolia/useSearch";
import useDarkMode from "hooks/useDarkMode";
import { MdLightMode, MdDarkMode } from "react-icons/md";

const CATEGORIES = [
  { value: "politic", title: "Քաղաքական", icon: politic },
  { value: "social", title: "Սոցիալական", icon: social },
  { value: "economic", title: "Տնտեսական", icon: economic },
  { value: "prophetic", title: "Մարգարեական", icon: prophetic },
  { value: "finance", title: "Ֆինանսական", icon: finance },
  { value: "sport", title: "Սպորտային", icon: sport },
  { value: "scientific", title: "Գիտական", icon: scientific },
];

const Promises = () => {
  const router = useRouter();
  const { theme: appTheme, toggleTheme, mounted } = useDarkMode();
  const isLight = appTheme === "light";
  const { predictor, categories, finished } = router.query;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [layoutMode, setLayoutMode] = useState(isMobile ? "shorts" : "grid"); // 'grid' | 'shorts'

  useEffect(() => {
    if (!isMobile && layoutMode === "shorts") {
      document.documentElement.classList.add("shorts-layout-active");
    } else {
      document.documentElement.classList.remove("shorts-layout-active");
    }
    return () => {
      document.documentElement.classList.remove("shorts-layout-active");
    };
  }, [layoutMode]);

  const scrollRef = useRef(null);
  const [filters, setFilters] = useState({
    predictorId: predictor,
    order: { orderBy: "createdAt", direction: "desc" },
  });
  const {
    promises,
    count,
    isLoading,
    getNextPage,
    page,
    setPage,
    hasMoreItems,
  } = usePromises(filters);

  useEffect(() => {
    if (categories) {
      setFilters((prev) => ({ ...prev, categories: categories.split(",") }));
    } else {
      setFilters((prev) => ({ ...prev, categories: [] }));
    }
  }, [categories]);

  useEffect(() => {
    if (finished === "true") {
      setFilters((prev) => ({ ...prev, finished: true }));
    } else {
      setFilters((prev) => ({ ...prev, finished: false }));
    }
  }, [finished]);

  const { setSearchText, searchText, searchResult, searchLoading } = useSearch({
    getData: getPromises,
  });

  useEffect(() => {
    if (layoutMode === "shorts") {
      document.body.classList.add("shorts-active");
    } else {
      document.body.classList.remove("shorts-active");
    }
    return () => {
      document.body.classList.remove("shorts-active");
    };
  }, [layoutMode]);

  const scrollUp = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        top: -scrollRef.current.clientHeight,
        behavior: "smooth",
      });
    }
  };

  const scrollDown = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        top: scrollRef.current.clientHeight,
        behavior: "smooth",
      });
    }
  };

  const renderFilters = () => (
    <div className="flex flex-col gap-[24px]">
      {predictor && promises?.[0] && (
        <button
          onClick={() => {
            router.push({
              query: {
                predictor: "",
                categories,
              },
            });
            setFilters((prev) => ({ ...prev, predictorId: "" }));
          }}
          style={{
            boxShadow: "0px 0px 10px 0px rgba(102, 133, 255, 0.15)",
          }}
          className="flex items-center gap-[10px] rounded-xl p-4 transition-all duration-300 bg-cyan-500/20 border border-cyan-400 w-full text-cyan-50 hover:bg-cyan-500/30"
        >
          <span className="text-[13px]">
            <span className="text-cyan-200">Կանխատեսող`</span>&nbsp;&nbsp;
            <span className="font-bold tracking-wider">
              {promises[0].predictor.firstName}
              {promises[0].predictor.lastName}
            </span>
          </span>
        </button>
      )}

      <div
        className={`w-full ${searchText && !searchLoading ? "hidden" : "flex"}`}
      >
        <FormControl
          sx={{
            width: "100%",
            backgroundColor: "rgba(102, 133, 255, 0.05)",
            borderRadius: "12px",
            padding: "0px",
          }}
          size="small"
        >
          <InputLabel
            id="order-select-label"
            sx={{ color: isLight ? "#475569" : "#94a3b8" }}
          >
            Տեսակավորում ըստ՝
          </InputLabel>
          <Select
            labelId="order-select-label"
            value={`${filters.order.orderBy}_${filters.order.direction}`}
            onChange={(e) => {
              const [orderBy, direction] = e.target.value.split("_");
              setFilters((prev) => ({
                ...prev,
                order: { orderBy, direction },
              }));
            }}
            label="Տեսակավորում ըստ՝"
            sx={{
              borderRadius: "12px",
              padding: "0px",
              color: isLight ? "#0f172a" : "#e2e8f0",
              fontFamily: "var(--tw-)",
              "& fieldset": {
                borderColor: "rgba(102, 133, 255, 0.2)",
                transition: "all 0.3s",
              },
              "&:hover fieldset": {
                borderColor: "rgba(102, 133, 255, 0.5) !important",
              },
              "&.Mui-focused fieldset": {
                borderColor: "rgba(102, 133, 255, 0.8) !important",
                boxShadow: "0 0 10px rgba(102, 133, 255, 0.2)",
              },
              "& .MuiSvgIcon-root": { color: "rgba(102, 133, 255, 0.8)" },
            }}
          >
            <MenuItem value="createdAt_desc">Ստեղծման ամսաթվի (նոր)</MenuItem>
            <MenuItem value="createdAt_asc">Ստեղծման ամսաթվի (հին)</MenuItem>
            <MenuItem value="deadline_desc">Վերջնաժամկետի (բարձր)</MenuItem>
            <MenuItem value="deadline_asc">Վերջնաժամկետի (ցածր)</MenuItem>
          </Select>
        </FormControl>
      </div>

      <div className="flex flex-col gap-4">
        <Typography
          variant="subtitle2"
          sx={{
            color: isLight ? "#475569" : "#94a3b8",
            textTransform: "uppercase",
            fontSize: "10px",
            fontWeight: "bold",
            letterSpacing: "1px",
          }}
        >
          Կատեգորիաներ
        </Typography>
        <div
          className={`flex flex-wrap gap-[12px] ${searchText && !searchLoading ? "hidden" : "flex"}`}
        >
          {CATEGORIES.map((category, index) => {
            const isActive = filters.categories?.includes(category.value);
            return (
              <button
                key={index}
                onClick={() => {
                  if (isActive) {
                    router.push({
                      pathname: "/promises",
                      query: {
                        predictor,
                        finished,
                        categories: filters.categories
                          .filter((item) => item !== category.value)
                          .join(","),
                      },
                    });
                  } else {
                    router.push({
                      pathname: "/promises",
                      query: {
                        predictor,
                        finished,
                        categories: [
                          ...[...(filters.categories || [])],
                          category.value,
                        ].join(","),
                      },
                    });
                  }
                }}
                className={`flex items-center gap-[8px] rounded-lg p-[6px] px-[10px] transition-all duration-300 border text-[12px] font-semibold ${isActive ? "bg-[#6685FF]/20 border-[#6685FF] text-[#6685FF]" : "bg-white/5 border-white/10 text-slate-400 hover:border-[#6685FF]/40 hover:text-cyan-200"}`}
              >
                <Image
                  width={16}
                  height={16}
                  src={category.icon}
                  alt={category.title}
                  priority
                  className="opacity-80"
                />
                <span className="leading-none">{category.title}</span>
              </button>
            );
          })}

          <button
            onClick={() => {
              const newFinished = !filters.finished;
              router.push({
                pathname: "/promises",
                query: {
                  predictor,
                  categories,
                  finished: newFinished,
                },
              });
            }}
            className={`flex items-center gap-[8px] rounded-lg p-[6px] px-[10px] transition-all duration-300 border text-[12px] font-semibold ${filters.finished ? "bg-[#6685FF]/20 border-[#6685FF] text-[#6685FF]" : "bg-white/5 border-white/10 text-slate-400 hover:border-[#6685FF]/40 hover:text-cyan-200"}`}
          >
            <span className="leading-none">Միայն Ավարտվածները</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <CustomHead title="Խոստումներ" />
      <div
        className={`transition-all duration-700 flex shadow-[0_0_30px_rgba(102,133,255,0.05)] light:shadow-sm ${
          layoutMode === "shorts"
            ? "flex-row items-start w-screen h-[100dvh] overflow-hidden p-0 gap-0 !border-0 !rounded-none fixed top-0 left-0 z-[60] bg-[#030014]"
            : "flex-col xl:flex-row p-4 md:p-[24px] gap-[24px] mb-[24px] md:rounded-[24px] rounded-none bg-[#050312]/80 light:bg-white/80 backdrop-blur-xl"
        }`}
      >
        {/* Filter Sidebar (Desktop) */}
        <div
          className={`hidden xl:flex flex-col w-[260px] shrink-0 gap-[20px] pr-6 border-r border-white/5 light:border-slate-200 ${layoutMode === "shorts" ? "h-full overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" : ""}`}
        >
          <div className="text-[24px] font-sans font-black tracking-tighter text-cyan-50 light:text-slate-900 drop-shadow-[0_0_8px_rgba(255,255,255,0.3)] light:drop-shadow-none">
            Խոստումներ
          </div>
          {renderFilters()}
        </div>

        <div
          className={`flex flex-col w-full ${layoutMode === "shorts" ? "h-full flex-1" : "gap-[24px]"}`}
        >
          {/* Header (Mobile & Controls) */}
          <div
            className={`flex flex-col md:flex-row justify-between items-start md:items-center gap-[16px] w-full ${layoutMode === "shorts" ? "p-4 md:p-0" : ""}`}
          >
            <div
              className={`flex items-center gap-[12px] xl:hidden ${layoutMode === "shorts" ? "hidden" : "flex"}`}
            >
              <div className="text-[18px] md:text-[24px] font-sans font-black tracking-tighter text-cyan-50 light:text-slate-900 drop-shadow-[0_0_8px_rgba(255,255,255,0.3)] light:drop-shadow-none">
                Խոստումներ
              </div>
              {searchText && (
                <div className="flex items-center justify-center bg-cyan-500/20 light:bg-cyan-50 border border-cyan-400 light:border-cyan-200 shadow-[0_0_10px_rgba(102,133,255,0.3)] light:shadow-none rounded-[16px] w-[36px] h-[36px] text-cyan-50 light:text-cyan-700  text-[16px] font-bold">
                  {searchResult.length}
                </div>
              )}
              {!searchText && !!count && (
                <div className="flex items-center justify-center bg-cyan-500/20 light:bg-cyan-50 border border-cyan-400 light:border-cyan-200 shadow-[0_0_10px_rgba(102,133,255,0.3)] light:shadow-none rounded-[16px] w-[36px] h-[36px] text-cyan-50 light:text-cyan-700  text-[16px] font-bold">
                  {count}
                </div>
              )}
            </div>

            <div className="hidden xl:flex items-center gap-[12px]">
              {searchText && (
                <div className="flex items-center justify-center bg-cyan-500/20 light:bg-cyan-50 border border-cyan-400 light:border-cyan-200 shadow-[0_0_10px_rgba(102,133,255,0.3)] light:shadow-none rounded-[16px] px-3 h-[36px] text-cyan-50 light:text-cyan-700  text-[16px] font-bold gap-2">
                  <span className="text-[12px] opacity-50 font-sans uppercase">
                    Արդյունք:
                  </span>
                  {searchResult.length}
                </div>
              )}
              {!searchText && !!count && (
                <div className="flex items-center justify-center bg-cyan-500/20 light:bg-cyan-50 border border-cyan-400 light:border-cyan-200 shadow-[0_0_10px_rgba(102,133,255,0.3)] light:shadow-none rounded-[16px] px-3 h-[36px] text-cyan-50 light:text-cyan-700  text-[16px] font-bold gap-2">
                  <span className="text-[12px] opacity-50 font-sans uppercase">
                    Ընդհանուր:
                  </span>
                  {count}
                </div>
              )}
            </div>

            <div className="flex flex-row items-center gap-[12px] w-full md:w-auto">
              <TextField
                placeholder="Որոնում"
                type="text"
                autoComplete="off"
                sx={{
                  backgroundColor: isLight
                    ? "rgba(0,0,0,0.03)"
                    : "rgba(102, 133, 255, 0.05)",
                  borderRadius: "14px",
                  "& fieldset": {
                    borderColor: isLight
                      ? "rgba(0,0,0,0.1)"
                      : "rgba(102, 133, 255, 0.2)",
                    transition: "all 0.3s",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(102, 133, 255, 0.5) !important",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "rgba(102, 133, 255, 0.8) !important",
                    boxShadow: "0 0 10px rgba(102, 133, 255, 0.2)",
                  },
                  "& .MuiInputBase-input": {
                    color: isLight ? "#0f172a" : "#e2e8f0",
                    fontFamily: "var(--tw-)",
                    padding: "10px 14px",
                    "&::placeholder": {
                      color: isLight ? "#64748b" : "#94a3b8",
                      opacity: 1,
                    },
                  },
                }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full md:w-[250px]"
              />
              <button
                onClick={() => setIsFilterDrawerOpen(true)}
                className={`flex items-center shrink-0 justify-center w-[44px] h-[44px] rounded-xl border transition-all duration-300 xl:hidden ${
                  isFilterDrawerOpen
                    ? "bg-cyan-500/30 border-cyan-400 text-cyan-50 shadow-[0_0_15px_rgba(102,133,255,0.3)]"
                    : "bg-cyan-500/10 border-cyan-500/20 text-cyan-50 hover:bg-cyan-500/20"
                }`}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="4" y1="21" x2="4" y2="14"></line>
                  <line x1="4" y1="10" x2="4" y2="3"></line>
                  <line x1="12" y1="21" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12" y2="3"></line>
                  <line x1="20" y1="21" x2="20" y2="16"></line>
                  <line x1="20" y1="12" x2="20" y2="3"></line>
                  <line x1="1" y1="14" x2="7" y2="14"></line>
                  <line x1="9" y1="8" x2="15" y2="8"></line>
                  <line x1="17" y1="16" x2="23" y2="16"></line>
                </svg>
              </button>
              <div className="bg-cyan-500/10 rounded-xl p-1 border border-cyan-500/20 hidden md:flex">
                <button
                  onClick={() => setLayoutMode("grid")}
                  className={`p-2 rounded-lg transition-all ${layoutMode === "grid" ? "bg-cyan-500 text-white shadow-[0_0_10px_rgba(102,133,255,0.4)]" : "text-cyan-500/60 hover:text-cyan-400"}`}
                  title="Ցանց"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                </button>
                <button
                  onClick={() => setLayoutMode("shorts")}
                  className={`p-2 rounded-lg transition-all ${layoutMode === "shorts" ? "bg-cyan-500 text-white shadow-[0_0_10px_rgba(102,133,255,0.4)]" : "text-cyan-500/60 hover:text-cyan-400"}`}
                  title="Shorts"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="5"
                      y="2"
                      width="14"
                      height="20"
                      rx="2"
                      ry="2"
                    ></rect>
                    <line x1="12" y1="18" x2="12.01" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 relative">
            {layoutMode === "grid" ? (
              <>
                {!!searchResult.length && searchText && (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-[24px] w-full mb-6">
                    {searchResult.map((promise) => {
                      return (
                        <PromiseCard key={promise.objectID} promise={promise} />
                      );
                    })}
                  </div>
                )}
                <div
                  className={`grid grid-cols-1 xl:grid-cols-2 gap-[24px] w-full ${searchText && !searchLoading ? "hidden" : "grid"}`}
                >
                  {promises.map((promise) => {
                    return <PromiseCard key={promise.id} promise={promise} />;
                  })}
                  {isLoading && skeletonGenerator(<ProgramSkeleton />)}
                  <InfiniteScrollTrigger
                    isLoading={isLoading}
                    hasMoreItems={hasMoreItems}
                    callback={getNextPage}
                    page={page}
                    setPage={setPage}
                  />
                </div>
              </>
            ) : (
              <div className="flex-1 relative h-full flex justify-center bg-[#030014] overflow-hidden w-full">
                {/* Floating Top Control Header for Desktop/Mobile Shorts */}
                <div className="hidden md:absolute top-4 left-4 right-4 flex justify-between items-center z-50 pointer-events-none">
                  {/* Category Title */}
                  <span className="text-white text-lg font-black tracking-tight drop-shadow-[0_0_10px_rgba(0,0,0,0.8)] pointer-events-auto select-none">
                    Խոստումներ
                  </span>

                  {/* Back to Grid / Exit Shorts Button */}
                  <button
                    onClick={() => setLayoutMode("grid")}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-black/60 hover:bg-[#6685FF] hover:scale-105 border border-white/20 text-white text-xs font-bold transition-all shadow-[0_0_15px_rgba(0,0,0,0.5)] pointer-events-auto"
                    title="Exit Shorts"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    <span>Փակել</span>
                  </button>
                </div>

                {/* Floating Vertical Navigation Blocks */}
                <div className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 hidden md:flex flex-col gap-[16px] z-50">
                  <button
                    onClick={scrollUp}
                    className="p-3 rounded-full bg-black/60 border border-cyan-500/30 text-cyan-50 hover:bg-cyan-500/40 hover:scale-110 transition-all shadow-[0_0_15px_rgba(102,133,255,0.2)]"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      width="24"
                      height="24"
                    >
                      <path d="M7 14l5-5 5 5z" />
                    </svg>
                  </button>
                  <button
                    onClick={scrollDown}
                    className="p-3 rounded-full bg-black/60 border border-cyan-500/30 text-cyan-50 hover:bg-cyan-500/40 hover:scale-110 transition-all shadow-[0_0_15px_rgba(102,133,255,0.2)]"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      width="24"
                      height="24"
                    >
                      <path d="M7 10l5 5 5-5z" />
                    </svg>
                  </button>
                </div>

                {/* Scroll Snap Carousel */}
                <div
                  ref={scrollRef}
                  className="w-full max-w-[450px] h-full overflow-y-scroll snap-y snap-mandatory scroll-smooth overscroll-contain [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] relative"
                >
                  {promises.map((promise) => (
                    <div
                      key={promise.id}
                      className="h-full w-full snap-always snap-center flex items-center justify-center shrink-0"
                    >
                      <PromiseShortsCard promise={promise} />
                    </div>
                  ))}
                </div>
                {/* Load more trigger — OUTSIDE snap container to avoid scroll jumps */}
                <div className="absolute bottom-0 left-0 right-0 pointer-events-none h-1">
                  <InfiniteScrollTrigger
                    isLoading={isLoading}
                    hasMoreItems={hasMoreItems}
                    callback={getNextPage}
                    page={page}
                    setPage={setPage}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Drawer
        anchor="left"
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#030014",
            borderRight: "1px solid rgba(102, 133, 255, 0.2)",
            width: "300px",
            padding: "24px",
          },
        }}
      >
        <div className="flex flex-col gap-8 h-full overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex items-center justify-between">
            <Typography
              variant="h6"
              sx={{ color: "#e2e8f0", fontWeight: "bold" }}
            >
              Ֆիլտրեր
            </Typography>
            <IconButton
              onClick={() => setIsFilterDrawerOpen(false)}
              sx={{ color: isLight ? "#475569" : "#94a3b8" }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </IconButton>
          </div>
          {renderFilters()}
          <button
            onClick={() => setIsFilterDrawerOpen(false)}
            className="mt-auto w-full py-4 rounded-xl bg-cyan-500/20 border border-cyan-400 text-cyan-50 font-bold hover:bg-cyan-500/30 transition-all"
          >
            Կիրառել
          </button>
        </div>
      </Drawer>
    </>
  );
};

export default Promises;
